// The side menu's muscle-memory shortcuts: Pinned Matters, then Recent
// Matters / Clients / Contacts.  Layout owns the four lists (and their
// storage listeners); they are drawn from those same lists in two places:
//
//   SidebarQuickLists  the full menu and the ☰ drawer, at the top of the
//                      menu's scrolling region.
//   RailQuickMenu      the icon rail (768-1279 px, lib/shellLayout.js), where
//                      titles do not fit: one ⭐ button opens a popover with the
//                      SAME sections, links and unpin / clear controls (James,
//                      2026-10-20: "⭐ button on the icon strip").
//
// Components/Layout.responsive.test.js pins both.

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'
import { useAsk } from '../context/PromptProvider'
import { unpinMatter, clearPinnedMatters } from '../lib/pinnedMatters'
import { clearRecentMatters } from '../lib/recentMatters'
import { recentClientsTracker, recentContactsTracker } from '../lib/recentItems'
import { hasPermission } from '../lib/navItems'
import { useOverlayMenu } from '../hooks/useShellLayout'
import { subscribeDisplayZoom } from '../lib/displayZoom'

// Each Recent section shows the newest five (the trackers keep ten).
export const RECENT_SHOWN = 5

// What the lists show this person: their pinned matters (with the `matters`
// permission) and up to RECENT_SHOWN recent matters / clients / contacts,
// each section only with its page's permission and only when it has items.
export function quickListSections (user, { pinnedMatters, recentMatters, recentClients, recentContacts } = {}) {
  const list = (v) => (Array.isArray(v) ? v : [])
  const pinned = hasPermission(user, 'matters') ? list(pinnedMatters) : []
  const recents = [
    hasPermission(user, 'matters') && list(recentMatters).length > 0 && {
      label: 'Matters', icon: '📁',
      items: list(recentMatters).slice(0, RECENT_SHOWN),
      path: (m) => `/matters/${m.id}`,
      clear: () => clearRecentMatters()
    },
    hasPermission(user, 'clients') && list(recentClients).length > 0 && {
      label: 'Clients', icon: '👥',
      items: list(recentClients).slice(0, RECENT_SHOWN),
      path: (c) => `/clients/${c.id}`,
      clear: () => { recentClientsTracker.clear() }
    },
    hasPermission(user, 'contacts') && list(recentContacts).length > 0 && {
      label: 'Contacts', icon: '📇',
      items: list(recentContacts).slice(0, RECENT_SHOWN),
      // No detail page for contacts — jump to the Contacts list (the person
      // finds + edits it there).  Still faster than scanning the list.
      path: () => '/contacts',
      clear: () => { recentContactsTracker.clear() }
    }
  ].filter(Boolean)
  return { pinned, recents }
}

// Whether this person can have anything in the lists at all: the rail's ⭐
// shows for them even while the lists are still empty (with a hint).
export function canHaveQuickLists (user) {
  return hasPermission(user, ['matters', 'clients', 'contacts'])
}

// The Pinned block, then the Recent block; nothing when both are empty.
// onRemove (the rail popover passes it): called with the × / ✕ / Unpin-all
// control just pressed, BEFORE its rows go; it returns a function called
// afterwards with true (rows removed) or false (Unpin all cancelled), so the
// popover can move focus on instead of leaving it on <body> with the removed
// control.
export function SidebarQuickLists ({ user, pinnedMatters, recentMatters, recentClients, recentContacts, onRemove }) {
  const ask = useAsk()
  const { pinned, recents } = quickListSections(user, { pinnedMatters, recentMatters, recentClients, recentContacts })
  if (pinned.length === 0 && recents.length === 0) return null
  const removing = (e) => {
    const control = e && e.currentTarget
    return typeof onRemove === 'function' && control ? onRemove(control) : () => {}
  }
  return (
    <>
      {/* Pinned Matters — sticky favourites: no auto-expiry, never pushed
          out by newer activity, ordered by pin time.  The per-matter × (on
          hover, or keyboard focus) removes just that pin; the section ✕
          clears every pin after a confirm.  Amber, so pins stand apart from
          the Recent (blue) block. */}
      {pinned.length > 0 && (
        <div className="px-4 pt-3">
          <div className="flex items-center justify-between px-4 pb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-300 flex items-center gap-1">
              <span>⭐</span> Pinned Matters
            </span>
            <button
              onClick={async (e) => {
                const done = removing(e)
                const yes = await ask({ type: 'confirm', title: 'Unpin all matters?', message: `Unpin all ${pinned.length} pinned matters?`, confirmLabel: 'Unpin all' })
                if (yes) clearPinnedMatters()
                done(yes === true)
              }}
              title="Clear all pins"
              className="text-primary-400 hover:text-white text-[10px] leading-none px-1"
              aria-label="Clear all pinned matters">
              ✕
            </button>
          </div>
          <div className="space-y-0.5">
            {pinned.map((m) => (
              <div key={m.id} className="group relative">
                <NavLink
                  to={`/matters/${m.id}`}
                  title={m.title}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs transition-colors ${
                      isActive
                        ? 'bg-amber-500 text-white'
                        : 'text-primary-200 hover:bg-primary-600 hover:text-white'
                    }`
                  }>
                  <span className="shrink-0 text-amber-300 group-hover:text-white">⭐</span>
                  <span className="truncate flex-1">{m.title}</span>
                </NavLink>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); const done = removing(e); unpinMatter(m.id); done(true) }}
                  title="Unpin this matter"
                  className="absolute right-1 top-1 text-primary-400 hover:text-white text-[10px] leading-none px-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                  aria-label={`Unpin ${m.title}`}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Matters / Clients / Contacts — one block with a heading per
          kind, each kind left out without its page permission or when it is
          empty.  Each ✕ clears that kind's list. */}
      {recents.length > 0 && (
        <div className="px-4 pt-3">
          {recents.map((sec) => (
            <div key={sec.label} className="mb-2">
              <div className="flex items-center justify-between px-4 pb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-300">Recent {sec.label}</span>
                <button
                  onClick={(e) => { const done = removing(e); sec.clear(); done(true) }}
                  title={`Clear recent ${sec.label.toLowerCase()} list`}
                  className="text-primary-400 hover:text-white text-[10px] leading-none px-1"
                  aria-label={`Clear recent ${sec.label.toLowerCase()}`}>
                  ✕
                </button>
              </div>
              <div className="space-y-0.5">
                {sec.items.map((it) => (
                  <NavLink
                    key={it.id}
                    to={sec.path(it)}
                    title={it.title}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs transition-colors ${
                        isActive
                          ? 'bg-primary-500 text-white'
                          : 'text-primary-200 hover:bg-primary-600 hover:text-white'
                      }`
                    }
                  >
                    <span className="shrink-0">{sec.icon}</span>
                    <span className="truncate">{it.title}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export const RAIL_QUICK_PANEL_ID = 'rail-quick-lists'
const EDGE = 12 // px kept clear of the window's edges
const MIN_PANEL_HEIGHT = 240 // a short window moves the popover up to fit this

// Where the popover goes: just right of the rail, its top level with the ⭐
// (moved up on a short window), never past the bottom edge.  Pure.
export function railPopoverPosition (buttonRect, railRight, viewportHeight) {
  const vh = Number(viewportHeight) > 0 ? Number(viewportHeight) : 768
  const btnTop = buttonRect && Number.isFinite(buttonRect.top) ? buttonRect.top : EDGE
  const right = Number.isFinite(railRight) ? railRight : (buttonRect && Number.isFinite(buttonRect.right) ? buttonRect.right : 64)
  const top = Math.max(EDGE, Math.min(btnTop, vh - EDGE - MIN_PANEL_HEIGHT))
  return { top: Math.round(top), left: Math.round(right + 8), maxHeight: Math.max(120, Math.round(vh - top - EDGE)) }
}

// The ⭐ rail button and its popover.  Layout renders it only in the icon
// rail (not the full menu or the drawer, which show the lists themselves).
// The popover is a small modal dialog (useOverlayMenu: focus moves to its
// first link, Tab stays inside, Esc closes, focus returns to ⭐) drawn in
// document.body, so the rail's scrolling and clipping never cut it.  A click
// outside it, choosing a link in it (also the page already open), any page
// change and the rail going away all close it.  ↑/↓ (Home/End) move between
// its links.  An unpin × or a clear ✕ removes the control that had focus:
// focus moves on to the next link left (else the one before it, else the
// popover's ✕), and a cancelled Unpin all gives it back to its ✕.
export function RailQuickMenu ({ user, pathname, pinnedMatters, recentMatters, recentClients, recentContacts }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const [refocus, setRefocus] = useState(null)
  const buttonRef = useRef(null)
  const panelRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  // SidebarQuickLists' onRemove: note the links after and before the pressed
  // control now, while they are all still there.
  const onRemove = useCallback((control) => {
    const panel = panelRef.current
    if (!panel || !control || !panel.contains(control)) return () => {}
    const follows = (l) => !!(control.compareDocumentPosition(l) & 4 /* DOCUMENT_POSITION_FOLLOWING */)
    const links = Array.from(panel.querySelectorAll('a[href]'))
    const after = links.filter(follows)
    const before = links.filter((l) => !follows(l)).reverse()
    return (removed) => setRefocus({ control, after, before, removed: removed !== false })
  }, [])

  // After the lists have re-rendered without those rows.
  useEffect(() => {
    if (!refocus) return
    setRefocus(null)
    const panel = panelRef.current
    if (!open || !panel) return
    const live = (el) => !!(el && el.isConnected && panel.contains(el))
    let target = null
    if (!refocus.removed) target = live(refocus.control) ? refocus.control : null
    else target = refocus.after.find(live) || refocus.before.find(live) || null
    if (!target) target = panel.querySelector('a[href]') || panel.querySelector('button')
    try { if (target) target.focus() } catch { /* gone */ }
  }, [refocus, open])

  const place = useCallback(() => {
    const btn = buttonRef.current
    if (!btn) return
    let rect = null
    let railRight
    try {
      rect = btn.getBoundingClientRect()
      const rail = typeof btn.closest === 'function' ? btn.closest('#app-sidebar') : null
      if (rail) railRight = rail.getBoundingClientRect().right
    } catch { /* no layout — the defaults below */ }
    const next = railPopoverPosition(rect, railRight, window.innerHeight)
    setPos((prev) => (prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight ? prev : next))
  }, [])

  const toggle = () => {
    if (open) { setOpen(false); return }
    place()
    setOpen(true)
  }

  useOverlayMenu({
    open,
    containerRef: panelRef,
    onClose: close,
    // Its first link (Enter opens it); the ✕ when the lists are empty.
    focusTarget: (root) => (root ? root.querySelector('a[href]') : null),
    returnFocusRef: buttonRef,
    keepFocusInside: false,
    lockScroll: false,
  })

  // Any page change closes it: a link in it, Back, a shortcut.
  useEffect(() => { setOpen(false) }, [pathname])

  // A click outside closes it — not a click on ⭐ (its own click toggles),
  // and not while a modal sits on top (the Unpin-all confirm owns its
  // clicks, backdrop included).  Attached after the opening event has passed.
  // The window resizing moves it with the rail, and so does the automatic
  // zoom (lib/displayZoom.js), which settles 200 ms after the resize — or on a
  // Display size change — and resizes the rem-wide rail under it.  The rail
  // animates to its new width (duration-200) after the zoom notifies, so the
  // rail's own size changes re-place it too (ResizeObserver, where there is one).
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      const panel = panelRef.current
      const t = e.target
      if (panel && panel.contains(t)) return
      if (buttonRef.current && buttonRef.current.contains(t)) return
      const modalOnTop = Array.from(document.querySelectorAll('[aria-modal="true"]'))
        .some((el) => el !== panel && !(panel && panel.contains(el)))
      if (modalOnTop) return
      setOpen(false)
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    window.addEventListener('resize', place)
    const offZoom = subscribeDisplayZoom(place)
    let railObserver = null
    const btn = buttonRef.current
    const rail = btn && typeof btn.closest === 'function' ? btn.closest('#app-sidebar') : null
    if (rail && typeof window.ResizeObserver === 'function') {
      try {
        railObserver = new window.ResizeObserver(() => place())
        railObserver.observe(rail)
      } catch { railObserver = null }
    }
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('resize', place)
      offZoom()
      if (railObserver) railObserver.disconnect()
    }
  }, [open, place])

  // Choosing a link closes it, also the page already open (no page change).
  const closeOnLinkClick = (e) => {
    const a = e && e.target && typeof e.target.closest === 'function' ? e.target.closest('a[href]') : null
    if (a && panelRef.current && panelRef.current.contains(a)) setOpen(false)
  }

  // ↑ / ↓ step through the links (wrapping), skipping the × / ✕ buttons;
  // Home / End jump to the first / last.
  const onKeyDown = (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
    const panel = panelRef.current
    if (!panel) return
    const links = Array.from(panel.querySelectorAll('a[href]'))
    if (links.length === 0) return
    e.preventDefault()
    const active = document.activeElement
    const n = links.length
    let i = links.indexOf(active)
    let next
    if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = n - 1
    else if (i >= 0) next = e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n
    else {
      // From a button: the next link after it (↓) or the last one before it (↑).
      const follows = (l) => !!(active && typeof active.compareDocumentPosition === 'function' &&
        (active.compareDocumentPosition(l) & 4 /* DOCUMENT_POSITION_FOLLOWING */))
      if (e.key === 'ArrowDown') { i = links.findIndex(follows); next = i >= 0 ? i : 0 } else {
        const before = links.filter((l) => !follows(l))
        next = before.length > 0 ? links.indexOf(before[before.length - 1]) : n - 1
      }
    }
    try { links[next].focus() } catch { /* gone */ }
  }

  if (!canHaveQuickLists(user)) return null
  const lists = { pinnedMatters, recentMatters, recentClients, recentContacts }
  const { pinned, recents } = quickListSections(user, lists)
  const empty = pinned.length === 0 && recents.length === 0

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        title="Pinned & recent matters, clients and contacts"
        aria-label="Pinned and recent"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? RAIL_QUICK_PANEL_ID : undefined}
        data-rail-tip="Pinned & recent"
        className={`mx-auto mt-2 [@media(max-height:760px)]:mt-1 hover:text-white hover:bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center text-sm transition-colors ${open ? 'bg-primary-600 text-white' : 'text-amber-300'}`}
      >
        <span aria-hidden="true">⭐</span>
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          id={RAIL_QUICK_PANEL_ID}
          role="dialog"
          aria-modal="true"
          aria-label="Pinned and recent"
          onKeyDown={onKeyDown}
          onClickCapture={closeOnLinkClick}
          style={pos ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight } : undefined}
          className="no-print fixed z-[62] w-72 max-w-[calc(100vw-5rem)] overflow-y-auto overscroll-contain rounded-xl bg-primary-700 text-white shadow-2xl ring-1 ring-black/20 pb-3"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-primary-600">
            <span className="text-sm font-semibold">Pinned &amp; recent</span>
            <button
              type="button"
              onClick={close}
              title="Close (Esc)"
              aria-label="Close pinned and recent"
              className="text-primary-100 hover:text-white hover:bg-primary-600 rounded-full w-7 h-7 flex items-center justify-center text-sm transition-colors">
              ✕
            </button>
          </div>
          {empty ? (
            <p className="px-4 pt-3 text-xs text-primary-200">
              Nothing here yet. Matters, clients and contacts you open show up here, and so do matters you pin with <strong>☆ Pin</strong> on the matter&apos;s page.
            </p>
          ) : (
            <SidebarQuickLists user={user} {...lists} onRemove={onRemove} />
          )}
        </div>,
        document.body
      )}
    </>
  )
}
